// Pure prompt-surface extraction — no I/O, no DOM. Shared single source of
// truth between the signal harness (hydratePrompt, which scans these surfaces)
// and the trace-detail highlighter (signalHighlight, which maps a signal's
// `loc` offsets back onto them). Keeping it here (not in triggerHarness, which
// pulls in analytics/pyodide/DOM) lets the highlighter import it cheaply.

// Text of a single parsed turn — text blocks plus any tool-result `content` string.
export function turnText(t) {
    if (!t) return "";
    const parts = [];
    const blocks = Array.isArray(t.blocks) ? t.blocks : [];
    for (const b of blocks) {
        if (b && b.type === "text" && typeof b.text === "string") parts.push(b.text);
    }
    if (typeof t.content === "string") parts.push(t.content);
    return parts.join("\n");
}

// The signal contract's prompt text: non-empty text blocks from the newest
// matching turn. This mirrors signals.inflate._newest_user_text exactly.
export function signalTurnText(t) {
    const blocks = Array.isArray(t?.blocks) ? t.blocks : [];
    return blocks
        .filter((block) => block?.type === "text" && typeof block.text === "string" && block.text)
        .map((block) => block.text)
        .join("\n");
}

export function lastSignalTurnTextWhere(turns, predicate) {
    const list = Array.isArray(turns) ? turns : [];
    for (let index = list.length - 1; index >= 0; index -= 1) {
        if (!predicate(list[index])) continue;
        const text = signalTurnText(list[index]);
        if (text) return { text, turnIndex: index };
    }
    return { text: "", turnIndex: -1 };
}

// tool_result content can be a string, an array of content blocks, or a nested
// object depending on provider — flatten to one searchable string.
export function toContentString(content) {
    if (content == null) return "";
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
        return content.map((b) => {
            if (typeof b === "string") return b;
            if (b && typeof b.text === "string") return b.text;
            try { return JSON.stringify(b); } catch { return ""; }
        }).join("\n");
    }
    try { return JSON.stringify(content); } catch { return ""; }
}

// The exact per-surface strings a signal scanned, with (turnIndex, blockIndex)
// provenance — so the trace-detail view can map a signal's `loc` offsets back to
// the rendered block and mark the span. Mirrors hydratePrompt's `text` (newest
// USER turn) and `tool_results` (newest tool-result turn) surfaces, reusing the
// SAME turnText / toContentString so offsets line up exactly.
export function eventSurfaceStrings(turns) {
    const list = Array.isArray(turns) ? turns : [];
    const userText = lastSignalTurnTextWhere(list, (turn) => turn?.role === "user");
    const toolResults = [];
    for (let i = list.length - 1; i >= 0; i--) {
        const blocks = Array.isArray(list[i]?.blocks) ? list[i].blocks : [];
        if (blocks.some((b) => b && b.type === "tool_result")) {
            blocks.forEach((b, bi) => {
                if (b && b.type === "tool_result") toolResults.push({ text: toContentString(b.content), turnIndex: i, blockIndex: bi });
            });
            break;
        }
    }
    return { userText, toolResults };
}

// Like eventSurfaceStrings, but spanning the WHOLE trace: userText is still the
// newest user turn, but toolResults are EVERY tool_result block across ALL
// turns in document order (turn asc, block asc) — not just the newest
// tool-result turn. The trace-detail highlighter uses this (rather than the
// newest-turn-only eventSurfaceStrings) so a secret sitting in an earlier
// turn's tool output still resolves to a (turnIndex, blockIndex) it can mark.
// The alert pipeline is unaffected — it keeps using eventSurfaceStrings /
// hydratePrompt's newest-turn scope.
export function traceSurfaces(turns) {
    const list = Array.isArray(turns) ? turns : [];
    const userText = lastSignalTurnTextWhere(list, (turn) => turn?.role === "user");
    const toolResults = [];
    list.forEach((t, ti) => {
        const blocks = Array.isArray(t?.blocks) ? t.blocks : [];
        blocks.forEach((b, bi) => {
            if (b && b.type === "tool_result") toolResults.push({ text: toContentString(b.content), turnIndex: ti, blockIndex: bi });
        });
    });
    return { userText, toolResults };
}

// The whole-trace surfaces shaped as the `event` fields the cheap alert signals
// scan: `text` (newest user turn) + `tool_results[].content` (every tool result
// in the trace). Spread over a hydrated event so ONE trigger pass covers the
// entire conversation, and each emitted loc.index maps back through traceSurfaces
// (SAME ordering + SAME strings) to the right block. Must be built from the same
// `turns` the highlighter maps against, or the loc offsets won't line up.
export function traceScanSurfaces(turns) {
    const { userText, toolResults } = traceSurfaces(turns);
    return { text: userText.text, tool_results: toolResults.map((tr) => ({ content: tr.text })) };
}
